import van, {type State} from "vanjs-core";
import {Net} from "@utils/net";
import {VanUtil as vu} from "@utils/vanUtil";
import {VanComponent, type VanComponentOptions} from "@utils/vanComponent";
import {LoginState} from "@login/loginState";
import {Busy} from "@commons/layout/busy";
import {TrimState} from "@utils/wrapper";
import type {LoginPageResponse} from "pnyx-dto";
import type {HolLoginResponse} from "hol-dto";
import {Icon} from "@commons/components/icons/icon";

type InternalForm = 
{
    userName: TrimState,
    password: State<string>,
    returnUrl: State<string|undefined>,
    locationUrlSegment: State<string|undefined>
};


type LoginPanelOptions = VanComponentOptions & 
{
    userName?: string;    
};

const { div, form, button, a, p, h1, label, input, progress } = van.tags;
export class LoginPanel extends VanComponent
{
    private readonly progress:HTMLElement;
    #model:LoginPageResponse|undefined;
    private readonly form:InternalForm;
    
    constructor(args: LoginPanelOptions = {})
    {
        super();
        
        this.form = 
        {
            userName: new TrimState(args.userName ?? ''),
            password: van.state(''),
            returnUrl: van.state(),
            locationUrlSegment: van.state()
        };

        this.progress = Busy.visibility(progress({ "aria-label": "Submitting form" }));
    }

    set model(value: LoginPageResponse)
    {
        this.#model = value;
        this.form.userName.val = value.userName ?? '';
        this.form.returnUrl.val = value.returnUrl;
        this.form.locationUrlSegment.val = value.locationUrlSegment;   
    }
    
    get model():LoginPageResponse|undefined
    {
        return this.#model;
    }
    
    get userName(): string
    {
        return this.form.userName.val ?? '';
    }

    toDom():HTMLElement
    {
        return div(this.toDomTags({"class": "login-form-pane"}),
            p({ class: 'login-kicker' }, 'Welcome back'),
            h1({ class: 'login-title', id: 'login-title' }, 'Sign in to Holos'),
            p({ class: 'login-intro' }, 'Use your account to continue to your workspace'),
            form({ class: 'login-form', onsubmit: event => this.submit(event) },
                div({class: "login-field"},
                    label({class: "login-label", for: 'login-username'}, 'Username'),
                    div({ class: 'login-input-wrap' },
                        input({ class: 'login-input form-control', id: 'login-username', tabIndex: 1,
                            type: 'text', required: 'required', autofocus: true,
                            spellcheck: false, autocapitalize: 'none',
                            value: () => this.form.userName.val ?? '',
                            oninput: e => this.form.userName.val = e.target.value
                        }),
                    ),
                    vu.load(vu.focalOnLoad)
                ),
                div({class: "login-field"},
                    div({ class: 'login-field__row' },
                        label({class: "login-label", for: 'login-password'}, 'Password'),
                        a({ class: 'login-forgot-link', tabIndex: 0, onclick: () => LoginState.instance.onForgotPasswordClick() }, 'Forgot password?')
                    ),
                    div({ class: 'login-password-row' },
                        vu.password({ class: 'form-control', id: 'login-password', tabIndex: 2,
                            required: 'required', bind: this.form.password
                        }),
                    ),
                ),                     
                div(
                    button({ class: "login-submit", type: 'submit', tabIndex: 3 }, 'Sign In', Icon.login().left(2).toDom()),
                    div({class: "mt-3 mb-1"}, this.progress),
                )
            )
        );
    }

    /**
     * @param event {Event}
     */
    async submit(event: Event): Promise<void>
    {
        let loginState = LoginState.instance;        
        loginState.alert.hide();
        event.preventDefault();     // stops browser from submitting form

        let toSubmit = vu.toJS(this.form);
        loginState.loginPayload = toSubmit;          // saves for next steps, if any 
        
        let loginResponse: HolLoginResponse = await Net.post<HolLoginResponse>({ url: '/login/login', data: JSON.stringify(toSubmit) });
        loginState.onLogin(loginResponse);
    }
}
